import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Game } from 'modules/games/domain/interfaces/game';
import { baseApi } from 'shared/api/base-api';
import { toApiError } from 'shared/errors';
import { RootState } from 'store/store';

import { Championship } from 'modules/championship/domain/interfaces/championship';
import { ChampionshipSettingDto } from 'modules/championship/domain/interfaces/championship-setting-dto';
import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';
import { SymbolicTeam } from 'modules/championship/domain/interfaces/symbolic-team';
import { UpsertChampionshipDto } from 'modules/championship/domain/interfaces/upsert-championship-dto';
import championshipRepository, {
  ChampionshipTeamsWithApplicationsDto,
  UpsertChampionshipParams,
} from 'modules/championship/domain/repositories/championship.repository';
import championshipGameRepository, { UpsertGameDto } from 'modules/championship/domain/repositories/championship-game.repository';
import symbolicTeamRepository, { UpsertSymbolicTeamDto } from 'modules/championship/domain/repositories/symbolic-team.repository';

const PREFIX = 'championship';

export const fetchChampionship = createAsyncThunk<Championship | null, number>(
  `${PREFIX}/fetchChampionship`,
  async (championshipId, { rejectWithValue }) => {
    try {
      return await championshipRepository.fetchChampionshipById(championshipId);
    } catch (err) {
      // Раньше отказ превращался в `null` — экран молчал. Теперь причину показывает место вызова.
      console.error(`${PREFIX}/fetchChampionship error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const fetchChampionshipTeams = createAsyncThunk<ChampionshipTeamsWithApplicationsDto, number>(
  `${PREFIX}/fetchChampionshipTeams`,
  async (championshipId, { rejectWithValue }) => {
    try {
      return await championshipRepository.fetchChampionshipTeamsWithApplications(championshipId);
    } catch (err) {
      console.error(`${PREFIX}/fetchChampionshipTeams error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const fetchChampionshipSettings = createAsyncThunk<
  { championshipId: number; settings: ChampionshipSettings; settingsMap: ChampionshipSettingDto[] },
  number
>(`${PREFIX}/fetchChampionshipSettings`, async (championshipId, { rejectWithValue }) => {
  try {
    const result = await championshipRepository.fetchChampionshipSettings(championshipId);
    return { championshipId, ...result };
  } catch (err) {
    console.error(`${PREFIX}/fetchChampionshipSettings error:`, err);
    return rejectWithValue(toApiError(err));
  }
});

export const upsertChampionshipGame = createAsyncThunk<Game, UpsertGameDto>(
  `${PREFIX}/upsertChampionshipGame`,
  async (payload, { rejectWithValue }) => {
    try {
      return await championshipGameRepository.upsertGame(payload);
    } catch (err) {
      console.error(`${PREFIX}/upsertChampionshipGame error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const deleteChampionshipGame = createAsyncThunk<number, { gameId: number; championshipId: number }>(
  `${PREFIX}/deleteChampionshipGame`,
  async (payload, { rejectWithValue }) => {
    try {
      return await championshipGameRepository.deleteGame(payload.gameId);
    } catch (err) {
      // Раньше отказ превращался в `null`, и причина терялась. `deleteMatch` проверяет только права
      // (403) — правило 5 часов (4015) бросают удаление этапа и тура, а не матча.
      console.error(`${PREFIX}/deleteGame error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const clearSelectedChampionship = createAction<void>(`${PREFIX}/clearSelectedChampionship`);

export const upsertChampionship = createAsyncThunk<number, UpsertChampionshipDto, { state: RootState }>(
  `${PREFIX}/upsertChampionship`,
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const newSettings = {
        ...state.championships.championship.data?.byId[params?.id ?? -1]?.settings,
        overlayId: params.overlayId,
      } as ChampionshipSettings;

      if (params.teamPlayersNumber) {
        newSettings.teamPlayersNumber = params.teamPlayersNumber;
      }

      let teamIds: number[] = [];
      if (params.id) {
        const championship = state.championships.championship.data!.byId[params.id];
        teamIds = championship.teams.map((team) => team.id);
      }

      const championshipId = await championshipRepository.upsertChampionship({
        id: params.id,
        tournamentId: params.tournamentId,
        name: params.name,
        description: params.description,
        startDate: params.startDate,
        endDate: params.endDate,
        address: params.address,
        settings: newSettings,
        locationIds: params.locationIds,
        teamIds,
      });

      dispatch(fetchChampionship(championshipId));
      // Инвалидируем список чемпионатов в RTK Query, чтобы новый чемпионат появился в списке
      dispatch(baseApi.util.invalidateTags([{ type: 'Championship', id: 'PARTIAL-LIST' }]));

      return championshipId;
    } catch (err) {
      console.error(`${PREFIX}/upsertChampionship error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const upsertChampionshipSettings = createAsyncThunk<void, UpsertChampionshipParams, { state: RootState }>(
  `${PREFIX}/upsertChampionshipSettings`,
  async (params, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const championship = params?.id ? state.championships.championship.data!.byId[params.id] : null;

      const championshipId = await championshipRepository.upsertChampionship({
        id: championship?.id,
        tournamentId: params.tournamentId,
        name: championship?.name ?? params.name,
        description: championship?.description || null,
        startDate: championship?.startDate ?? params.startDate,
        endDate: championship?.endDate ?? params.endDate,
        address: championship?.address ?? params.address,
        settings: params.settings,
        teamIds: championship?.teams.map((team) => team.id) ?? [],
      });

      await dispatch(fetchChampionshipSettings(championshipId));
    } catch (err) {
      console.error(`${PREFIX}/upsertChampionshipSettings error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const addChampionshipTeam = createAsyncThunk<void, { teamId: number; championshipId: number }, { state: RootState }>(
  `${PREFIX}/addChampionshipTeam`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const championship = state.championships.championship.data!.byId[payload.championshipId];
      const teamIds = new Set(championship.teams.map((team) => team.id));
      teamIds.add(payload.teamId);

      const params = {
        id: championship.id,
        tournamentId: championship.tournamentId,
        name: championship.name,
        description: championship.description || null,
        startDate: championship.startDate,
        endDate: championship.endDate,
        address: championship.address,
        settings: championship.settings,
        teamIds: Array.from(teamIds),
      };

      const championshipId = await championshipRepository.upsertChampionship(params);
      dispatch(fetchChampionship(championshipId));
    } catch (err) {
      console.error(`${PREFIX}/addChampionshipTeam error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

/**
 * Добавляет в соревнование сразу несколько команд — **одной** мутацией.
 *
 * Отличие от `addChampionshipTeam` (его зовёт первый флоу заявок) в том, что тот умеет ровно одну
 * команду за вызов, а `teamIds` собирает из стора — поэтому вызывающему приходилось ждать
 * перечитывания соревнования между итерациями, и на N команд уходило ~4N запросов.
 * `upsertChampionship` принимает список (`docs/schema.graphql:1583-1594`), так что цикл не нужен:
 * либо добавились все, либо ни одной. Старый экшен не трогаем — его зовёт v1.
 */
export const addChampionshipTeams = createAsyncThunk<void, { teamIds: number[]; championshipId: number }, { state: RootState }>(
  `${PREFIX}/addChampionshipTeams`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const championship = state.championships.championship.data!.byId[payload.championshipId];
      const teamIds = new Set(championship.teams.map((team) => team.id));
      payload.teamIds.forEach((teamId) => teamIds.add(teamId));

      await championshipRepository.upsertChampionship({
        id: championship.id,
        tournamentId: championship.tournamentId,
        name: championship.name,
        description: championship.description || null,
        startDate: championship.startDate,
        endDate: championship.endDate,
        address: championship.address,
        settings: championship.settings,
        teamIds: Array.from(teamIds),
      });

      // Ждём перечитывания: стор соревнования — источник `teamIds` для следующих правок состава.
      await dispatch(fetchChampionship(payload.championshipId));
    } catch (err) {
      console.error(`${PREFIX}/addChampionshipTeams error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const selectSymbolicTeam = createAction<{ symbolicTeam: SymbolicTeam | null; championshipId: number }>(
  `${PREFIX}/selectSymbolicTeam`,
);
export const showChampionshipSymbolicTeamForm = createAction<{ state: boolean; championshipId: number }>(
  `${PREFIX}/showChampionshipSymbolicTeamForm`,
);

export const upsertSymbolicTeam = createAsyncThunk<SymbolicTeam, UpsertSymbolicTeamDto>(
  `${PREFIX}/upsertSymbolicTeam`,
  async (params, { rejectWithValue }) => {
    try {
      return await symbolicTeamRepository.upsertSymbolicTeam(params);
    } catch (err) {
      console.error(`${PREFIX}/upsertSymbolicTeam error:`, err);
      return rejectWithValue(toApiError(err));
    }
  },
);

export const deleteChampionship = createAsyncThunk<boolean, { championshipId: number }>(
  `${PREFIX}/deleteChampionship`,
  async (payload, { rejectWithValue }) => {
    try {
      return await championshipRepository.deleteChampionship(payload.championshipId);
    } catch (error) {
      console.error(`${PREFIX}/deleteChampionship error:`, error);
      return rejectWithValue(toApiError(error));
    }
  },
);
